import Breadcrumbs from 'components/Breadcrumbs';
import { List } from 'immutable';
import React, { useEffect, useState } from 'react';
import { ProjectDriveFile } from 'records/ProjectDriveFile';
import { ProjectDriveFolderDetails } from 'records/ProjectDriveFolderDetails';
import { ProjectDriveFolderItems } from 'records/ProjectDriveFolderItems';
import { ProjectDriveSelectedItem } from 'records/ProjectDriveSelectedItem';
import { searchProjectDrive } from 'services/projectDriveService';
import FolderTrie from 'utils/FolderTrie';
import { hasImageExtension } from 'utils/image';
import withAppStore, { WithAppStoreProps } from 'utils/withAppStore';
import {
  FileBrowser,
  FileBrowserItem,
  FileBrowserMessage,
} from './FileBrowser';

type ProjectItem =
  | {
      type: 'file';
      value: ProjectDriveFile;
    }
  | {
      type: 'folder';
      value: ProjectDriveFolderDetails;
    };

type Breadcrumb = {
  label: string;
  id: string;
};

interface Props extends WithAppStoreProps {
  providerType: string;
  projectDriveId: string;
  initialSelectedItems?: ProjectDriveSelectedItem[];
  message?: FileBrowserMessage;
  filter?: string;
  onlyImageFiles?: boolean;
  foldersUnselectable?: boolean;
  clearSearchFilter(): void;
}

const ProjectFileBrowser: React.FC<Props> = ({
  actions,
  appStore,
  providerType,
  projectDriveId,
  initialSelectedItems,
  message,
  filter,
  onlyImageFiles,
  foldersUnselectable,
  clearSearchFilter,
}: Props) => {
  const { state } = appStore;
  const { projectDriveCurrentItems } = state;

  const [loadingFolderId, setLoadingFolderId] = useState<string | undefined>();
  const [currentItems, setCurrentItems] = useState<List<ProjectItem>>(
    List<ProjectItem>()
  );
  const [breadcrumbs, setBreadcrumbs] = useState<List<Breadcrumb>>(
    List<Breadcrumb>()
  );
  const [selectedItems, setSelectedItems] = useState<
    FolderTrie<ProjectDriveSelectedItem>
  >(new FolderTrie());

  const setSelectedItemsToInitial = () => {
    setSelectedItems(
      initialSelectedItems
        ? new FolderTrie<ProjectDriveSelectedItem>().insertAll(
            initialSelectedItems.map((item) => ({
              folderPathIds: item.folderPathIds.toArray(),
              value: item,
            }))
          )
        : new FolderTrie()
    );
  };

  const getProjectDriveFolderItems = (driveId: string, folderId?: string) => {
    setLoadingFolderId(folderId || driveId);

    actions.getProjectDriveFolderItems(driveId, folderId);
  };

  const performSearch = async (driveId: string, searchFilter: string) => {
    const { selectedProject } = state;
    const searchResults = await searchProjectDrive(
      selectedProject,
      driveId,
      searchFilter
    );
    const searchResultsList = List(searchResults);
    const fileItems: List<ProjectItem> = searchResultsList
      .filter((f) => f.committed)
      .map((f) => ({
        type: 'file',
        value: f,
      }));
    setBreadcrumbs(breadcrumbs.clear());
    setCurrentItems(fileItems);
  };

  const loadRoot = async () => {
    await getProjectDriveFolderItems(projectDriveId);
  };

  useEffect(() => {
    setSelectedItemsToInitial();
    loadRoot();
  }, [projectDriveId]);

  const setCurrentItemsFromFolder = (folder: ProjectDriveFolderItems) => {
    const { folders, documents } = folder;
    const folderItems: List<ProjectItem> = folders.map((f) => ({
      type: 'folder',
      value: f,
    }));
    const fileItems: List<ProjectItem> = documents
      .filter((d) => d.committed)
      .map((d) => ({
        type: 'file',
        value: d,
      }));

    clearSearchFilter();
    setCurrentItems(folderItems.concat(fileItems));
  };

  useEffect(() => {
    if (projectDriveCurrentItems.id === loadingFolderId) {
      setLoadingFolderId(undefined);
      setCurrentItemsFromFolder(projectDriveCurrentItems);
    }
  }, [projectDriveCurrentItems]);

  useEffect(() => {
    actions.setProjectDriveSelectedItems(
      List(selectedItems.descendantLeaves())
    );
  }, [selectedItems]);

  useEffect(() => {
    if (filter && filter.trim() !== '') {
      performSearch(projectDriveId, filter.trim());
    } else {
      loadRoot();
    }
  }, [filter]);

  const getPathIdsFromBreadcrumbs = () => {
    return breadcrumbs.map(({ id }) => id).toArray();
  };

  const isAncestorSelected = getPathIdsFromBreadcrumbs().some((pathId) =>
    selectedItems.has(pathId)
  );

  const getItemSelectability = (name: string, type: 'file' | 'folder') => {
    const nameEndsWithImageExt = hasImageExtension(name);
    const isImageFile = type === 'file' && nameEndsWithImageExt;

    const isSelectableDisregardingAncestors =
      (!onlyImageFiles || isImageFile || type === 'folder') &&
      !(foldersUnselectable && type === 'folder');
    const isSelectable =
      !isAncestorSelected && isSelectableDisregardingAncestors;

    const nonImageFile = type === 'file' && !nameEndsWithImageExt;

    return {
      nonImageFile,
      isImageFile,
      isSelectableDisregardingAncestors,
      isSelectable,
    };
  };

  const fileBrowserItems: List<FileBrowserItem> = currentItems.map((item) => {
    const { type, value } = item;

    const {
      isSelectable,
      isSelectableDisregardingAncestors,
      isImageFile,
      nonImageFile,
    } = getItemSelectability(value.name, type);
    const onSelect = isSelectable
      ? () => {
          if (type === 'file' || type === 'folder') {
            const isSelected = !selectedItems.has(value.id);

            if (isSelected) {
              const pathIds = getPathIdsFromBreadcrumbs();
              setSelectedItems(
                selectedItems.insert(
                  pathIds,
                  ProjectDriveSelectedItem.deserialize({
                    itemType: type,
                    id: value.id,
                    name: value.name,
                    driveId: projectDriveId,
                    folderPathIds: pathIds,
                  })
                )
              );
            } else {
              setSelectedItems(selectedItems.delete(value.id));
            }
          }
        }
      : undefined;

    const onClick = (() => {
      if (type === 'folder') {
        return () => {
          getProjectDriveFolderItems(projectDriveId, value.id);
          setBreadcrumbs(
            breadcrumbs.push({
              label: value.name,
              id: value.id,
            })
          );
        };
      }
      if (isSelectable && type === 'file' && (!onlyImageFiles || isImageFile)) {
        return () => {
          const isSelected = !selectedItems.has(value.id);

          if (isSelected) {
            const pathIds = getPathIdsFromBreadcrumbs();
            setSelectedItems(
              selectedItems.insert(
                pathIds,
                ProjectDriveSelectedItem.deserialize({
                  itemType: type,
                  id: value.id,
                  name: value.name,
                  driveId: projectDriveId,
                  folderPathIds: getPathIdsFromBreadcrumbs(),
                })
              )
            );
          } else {
            setSelectedItems(selectedItems.delete(value.id));
          }
        };
      }
      return undefined;
    })();

    const indeterminate =
      type === 'folder' ? !selectedItems.isEmpty(value.id) : undefined;

    const isSelected = isAncestorSelected || selectedItems.has(value.id);
    const hideCheckbox = onlyImageFiles && nonImageFile;
    const fadedText = !isSelectableDisregardingAncestors && !onClick;

    return {
      isSelected,
      onSelect,
      onClick,
      type,
      value,
      indeterminate,
      hideCheckbox,
      fadedText,
    } as FileBrowserItem;
  });

  const areAllSelected =
    fileBrowserItems.filter((item) => item.isSelected).count() > 0 &&
    fileBrowserItems.every((item) => !item.onSelect || item.isSelected);

  const handleSelectAllClick = isAncestorSelected
    ? undefined
    : () => {
        const isAddAllAsSelected = !areAllSelected;
        const isRemoveAllSelected = areAllSelected;

        const itemsToChange = currentItems.reduce((toChange, currentItem) => {
          const { type, value } = currentItem;

          const { isSelectable } = getItemSelectability(value.name, type);

          if (
            isAddAllAsSelected &&
            !selectedItems.has(value.id) &&
            isSelectable
          ) {
            return toChange.push(currentItem);
          }
          if (isRemoveAllSelected && selectedItems.has(value.id)) {
            return toChange.push(currentItem);
          }
          return toChange;
        }, List<ProjectItem>());

        if (isAddAllAsSelected) {
          const pathIds = getPathIdsFromBreadcrumbs();
          setSelectedItems(
            selectedItems.insertAll(
              itemsToChange
                .map((item) => {
                  const { type, value } = item;
                  return {
                    folderPathIds: pathIds,
                    value: ProjectDriveSelectedItem.deserialize({
                      itemType: type,
                      id: value.id,
                      name: value.name,
                      driveId: projectDriveId,
                      folderPathIds: pathIds,
                    }),
                  };
                })
                .toArray()
            )
          );
        }
        if (isRemoveAllSelected) {
          const idsToRemove = itemsToChange.map((i) => i.value.id);
          setSelectedItems(selectedItems.deleteAll(idsToRemove.toArray()));
        }
      };

  const onProviderClick = () => {
    loadRoot();
    setBreadcrumbs(breadcrumbs.clear());
  };

  const onBreadcrumbClick = (folderId: string) => {
    getProjectDriveFolderItems(projectDriveId, folderId);

    const breadcrumbIndex = breadcrumbs.findIndex((b) => b.id === folderId);
    setBreadcrumbs(breadcrumbs.slice(0, breadcrumbIndex + 1));
  };

  const fileBrowserMessage: FileBrowserMessage | undefined = loadingFolderId
    ? { type: 'loading', text: 'loading' }
    : undefined;

  return (
    <>
      <div className="my2">
        <Breadcrumbs
          provider={{
            type: providerType,
            onClick: onProviderClick,
          }}
          folders={{
            value: breadcrumbs.toArray(),
            onClick: (breadcrumb) => onBreadcrumbClick(breadcrumb.id),
          }}
        />
      </div>
      <FileBrowser
        items={fileBrowserItems}
        areAllSelected={areAllSelected}
        onSelectAllClick={handleSelectAllClick}
        message={message || fileBrowserMessage}
        filter=""
      />
    </>
  );
};

export default withAppStore<Props>(ProjectFileBrowser);
